import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

interface Props {
  enabled: boolean;
  debounceMs?: number;
}

export const useNMEATimeSearch = ({ enabled, debounceMs = 300 }: Props) => {
  const [timeQuery, setTimeQuery] = useState("");
  const [debouncedTimeQuery, setDebouncedTimeQuery] = useState("");
  const timeQueryDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const completeQueryRegex = /^(?:\d{2}|\d{4}|\d{2,4}[ -]\d{2,4})$/;

  const isAllowedTypingState = (value: string): boolean => {
    if (value === "") return true;
    if (!/^[\d -]+$/.test(value)) return false;

    const separatorMatches = value.match(/[ -]/g) ?? [];
    if (separatorMatches.length > 1) return false;
    if (value.includes("-") && value.includes(" ")) return false;

    const separatorIndex = value.search(/[ -]/);
    if (separatorIndex === -1) {
      const token = value;
      if (token.length > 4) return false;
      if ([0, 1, 3].includes(token.length)) {
        return /^\d+$/.test(token);
      }
      if ([2, 4].includes(token.length)) {
        return /^\d+$/.test(token);
      }
      return false;
    }

    const separator = value[separatorIndex];
    const left = value.slice(0, separatorIndex);
    const right = value.slice(separatorIndex + 1);

    if (![2, 4].includes(left.length) || !/^\d+$/.test(left)) return false;
    if (separatorIndex !== left.length) return false;
    if (right.length > 4) return false;

    if (right.length === 0) return true;
    if ([1, 3].includes(right.length)) return /^\d+$/.test(right);
    if ([2, 4].includes(right.length)) return /^\d+$/.test(right);

    return false;
  };

  const handleTimeQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget.value
      .replace(/[^\d\s-]/g, "")
      .replace(/\s+/g, " ")
      .slice(0, 10);

    if (!isAllowedTypingState(nextValue)) {
      return;
    }

    setTimeQuery(nextValue);
  };

  const clearTimeQuery = () => {
    if (timeQueryDebounceRef.current) {
      clearTimeout(timeQueryDebounceRef.current);
      timeQueryDebounceRef.current = null;
    }
    setTimeQuery("");
    setDebouncedTimeQuery("");
  };

  useEffect(() => {
    if (!enabled) return;
    if (timeQueryDebounceRef.current) {
      clearTimeout(timeQueryDebounceRef.current);
    }

    timeQueryDebounceRef.current = setTimeout(() => {
      const trimmedQuery = timeQuery.trim();
      if (trimmedQuery.length === 0 || completeQueryRegex.test(trimmedQuery)) {
        setDebouncedTimeQuery(trimmedQuery);
      }
    }, debounceMs);

    return () => {
      if (timeQueryDebounceRef.current) {
        clearTimeout(timeQueryDebounceRef.current);
      }
    };
  }, [timeQuery, enabled, debounceMs]);

  return {
    timeQuery,
    debouncedTimeQuery,
    handleTimeQueryChange,
    clearTimeQuery,
  };
};
